import { useEffect, useMemo, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import api from "../../../../api/axios";
import { useCurrentTenant } from "../../../../hooks/useCurrentTenant";

const normStr = (v) => String(v ?? "").trim();
const normKey = (v) => normStr(v).toLowerCase();
const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const pickCI = (row, aliases) => {
  const lower = Object.create(null);
  for (const [k, v] of Object.entries(row ?? {})) lower[String(k).toLowerCase()] = v;
  for (const a of aliases) {
    const val = lower[String(a).toLowerCase()];
    if (val != null) return val;
  }
  return undefined;
};

export default function useDevicesExcel({ project, devices, enclosure, refreshProject }) {
  const tenantId = useCurrentTenant();
  const [subGroups, setSubGroups] = useState([]);

  useEffect(() => {
    api.get("/subgroup").then((r) => setSubGroups(r.data)).catch(() => {});
  }, []);

  const tenantSubGroups = useMemo(
    () => subGroups.filter((s) => s.tenantId === tenantId && s.ownership === "Tenant"),
    [subGroups, tenantId]
  );

  const importFile = useCallback(async (file) => {
    if (!file || !project?.id) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer());
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);

      // 1) normalize + validate (enclosure rows are ignored)
      const imported = rows
        .map((r) => ({
          type: normKey(pickCI(r, ["type"])),
          quantity: numOr0(pickCI(r, ["quantity", "qty", "q"])),
          reserve: numOr0(pickCI(r, ["expansionreserve", "placeholderquantity", "placeholders", "reserve", "expreserve"])),
          devicename: normStr(pickCI(r, ["devicename", "device", "name"])),
          partnumber: normStr(pickCI(r, ["partnumber", "article", "articlenumber", "pn"])),
        }))
        .filter((r) => r.type !== "enclosure")
        .filter((r) => (r.quantity > 0 || r.reserve > 0) && (r.devicename || r.partnumber));

      // 2) aggregate duplicates
      const aggregated = new Map();
      for (const r of imported) {
        const key = r.partnumber ? `pn:${normKey(r.partnumber)}` : `dn:${normKey(r.devicename)}`;
        const prev = aggregated.get(key);
        if (prev) { prev.quantity += r.quantity; prev.reserve += r.reserve; }
        else aggregated.set(key, { ...r });
      }

      // 3) lookups
      const sgByPart = new Map(tenantSubGroups.filter((s) => s.articleNumber).map((s) => [normKey(s.articleNumber), s]));
      const sgByName = new Map(tenantSubGroups.filter((s) => s.name).map((s) => [normKey(s.name), s]));
      const current = devices ?? [];
      const devByPart = new Map(
        current
          .filter((d) => d.articleNumber || d.subGroup?.articleNumber)
          .map((d) => [normKey(d.articleNumber || d.subGroup?.articleNumber), d])
      );
      const devBySg = new Map(current.map((d) => [d.subGroup?.id, d]));

      // 4) merge
      for (const { quantity, reserve, devicename, partnumber } of aggregated.values()) {
        let sg = partnumber ? sgByPart.get(normKey(partnumber)) : null;
        if (!sg && devicename) sg = sgByName.get(normKey(devicename));
        if (!sg) {
          console.warn("Import skip: no SubGroup match for", partnumber || devicename);
          continue;
        }

        const existing =
          (partnumber && devByPart.get(normKey(partnumber))) || devBySg.get(sg.id) || null;

        if (existing) {
          await api.put(`/projects/${project.id}/enclosure/devices/${existing.id}`, {
            ...existing,
            quantity,
            placeholderQuantity: reserve,
            articleNumber: sg.articleNumber ?? existing.articleNumber ?? "",
            deviceType: sg.name ?? "",
            subGroup: sg,
          });
        } else {
          await api.post(`/projects/${project.id}/enclosure/devices`, {
            quantity,
            placeholderQuantity: reserve,
            deviceType: sg.name ?? "",
            articleNumber: sg.articleNumber ?? "",
            subGroup: sg,
            enclosureId: project.enclosurePlacement?.id,
          });
        }
      }

      await refreshProject();
      alert("Excel import completed!");
    } catch (err) {
      console.error("Failed to import Excel:", err);
      alert("Import failed — see console for details.");
    }
  }, [project, devices, tenantSubGroups, refreshProject]);

  const exportFile = useCallback(() => {
    if (!project?.enclosurePlacement) return;

    const enclosureRow = {
      type: "enclosure",
      quantity: 1,
      expansionreserve: 0,
      devicename: enclosure?.name ?? "",
      partnumber: enclosure?.articleNumber ?? enclosure?.partNumber ?? "",
    };

    const deviceRows = (devices ?? []).map((d) => ({
      type: "device",
      quantity: Number(d.quantity) || 0,
      expansionreserve: Number(d.placeholderQuantity) || 0,
      devicename: d?.subGroup?.name ?? d?.deviceType ?? "",
      partnumber: normStr(d?.articleNumber) || d?.subGroup?.articleNumber || "",
    }));

    const ws = XLSX.utils.json_to_sheet([enclosureRow, ...deviceRows], {
      header: ["type", "quantity", "expansionreserve", "devicename", "partnumber"],
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Devices");
    XLSX.writeFile(wb, `${project?.name?.trim() || "project"}_devices.xlsx`);
  }, [project, devices, enclosure]);

  return { importFile, exportFile };
}